import React from 'react';
import { Ruler, Calendar, Flame, ArrowDown } from 'lucide-react';
import { useSimulateur } from '../SimulateurContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export const Step3PropertyDetails = () => {
  const { formData, updateFormData, goToNextStep, goToPrevStep } = useSimulateur();

  const handleContinue = () => {
    if (formData.surface && formData.constructionYear) {
      goToNextStep();
    }
  };

  const yearOptions = [
    { id: 'after2007', label: 'Après 2007', value: 2010 },
    { id: '1997-2007', label: '1997 - 2007', value: 2000 },
    { id: '1949-1997', label: '1949 - 1997', value: 1970 },
    { id: 'before1949', label: 'Avant 1949', value: 1940 },
  ];

  const getSelectedYearOption = () => {
    const year = formData.constructionYear;
    if (!year) return null;
    if (year >= 2007) return 'after2007';
    if (year >= 1997) return '1997-2007';
    if (year >= 1949) return '1949-1997';
    return 'before1949';
  };

  const handleYearSelect = (option) => {
    updateFormData('constructionYear', option.value);
  };

  const isValid = formData.surface && formData.constructionYear && formData.hasGas !== null;

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl md:text-3xl font-bold text-stone-900">
          Détails du bien
        </h2>
        <p className="text-stone-600">
          Ces informations permettent de calculer précisément votre devis
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-8">
        {/* Surface */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-base font-semibold text-stone-700">
            <Ruler className="w-5 h-5 text-emerald-600" />
            Surface du bien (m²)
          </Label>
          <div className="relative">
            <Input
              type="number"
              placeholder="Ex: 75"
              value={formData.surface}
              onChange={(e) => updateFormData('surface', e.target.value)}
              data-testid="input-surface"
              className="h-14 text-lg pl-4 pr-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 font-medium">
              m²
            </span>
          </div>
        </div>

        {/* Construction Year */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-base font-semibold text-stone-700">
            <Calendar className="w-5 h-5 text-emerald-600" />
            Période de construction
          </Label>
          <div className="grid grid-cols-2 gap-3">
            {yearOptions.map((option) => {
              const isSelected = getSelectedYearOption() === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => handleYearSelect(option)}
                  data-testid={`year-${option.id}`}
                  className={`
                    p-4 rounded-xl border-2 transition-all duration-200 text-center
                    ${isSelected 
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' 
                      : 'border-stone-200 hover:border-emerald-300 text-stone-700'
                    }
                  `}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Has Gas */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-base font-semibold text-stone-700">
            <Flame className="w-5 h-5 text-emerald-600" />
            Le bien dispose-t-il d'une installation gaz ?
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => updateFormData('hasGas', true)}
              data-testid="has-gas-yes"
              className={`
                p-4 rounded-xl border-2 transition-all duration-200
                ${formData.hasGas === true 
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' 
                  : 'border-stone-200 hover:border-emerald-300 text-stone-700'
                }
              `}
            >
              Oui
            </button>
            <button
              onClick={() => updateFormData('hasGas', false)}
              data-testid="has-gas-no"
              className={`
                p-4 rounded-xl border-2 transition-all duration-200
                ${formData.hasGas === false 
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' 
                  : 'border-stone-200 hover:border-emerald-300 text-stone-700'
                }
              `}
            >
              Non
            </button>
          </div>
        </div>

        {/* Has Cellar - Only for apartments */}
        {formData.propertyType === 'appartement' && (
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-semibold text-stone-700">
              <ArrowDown className="w-5 h-5 text-emerald-600" />
              L'appartement dispose-t-il d'une cave ?
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => updateFormData('hasCellar', true)}
                data-testid="has-cellar-yes"
                className={`
                  p-4 rounded-xl border-2 transition-all duration-200
                  ${formData.hasCellar === true 
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' 
                    : 'border-stone-200 hover:border-emerald-300 text-stone-700'
                  }
                `}
              >
                Oui
              </button>
              <button
                onClick={() => updateFormData('hasCellar', false)}
                data-testid="has-cellar-no"
                className={`
                  p-4 rounded-xl border-2 transition-all duration-200
                  ${formData.hasCellar === false 
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' 
                    : 'border-stone-200 hover:border-emerald-300 text-stone-700'
                  }
                `}
              >
                Non
              </button>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={goToPrevStep}
            className="px-6 py-3 rounded-xl"
            data-testid="btn-prev-step3"
          >
            Retour
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!isValid}
            className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            data-testid="btn-next-step3"
          >
            Continuer
          </Button>
        </div>
      </div>
    </div>
  );
};
